import classNames from 'classnames'
import { styled, UploadIcon } from 'opensolar-ui'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useNotify, useTranslate, useTranslateParse } from 'react-admin'
import {
  createPendingFileRecord,
  FileRecord,
  ResourcePrefix,
  UploadStatus,
  useFileUploadContext,
} from '../controller/UploadFileContext'
import { formatFileSize, isUploadDisabled } from '../utils'

const FileInputPlaceholderContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
  display: 'flex',
  flexDirection: compact ? 'row' : 'column',
  alignItems: 'center',
  justifyContent: compact ? 'flex-start' : 'center',
  gap: compact ? 8 : 0,
  border: compact ? '1px dashed' : '3px dashed',
  borderColor: theme.palette.grey[300],
  backgroundColor: 'white',
  boxSizing: 'border-box',
  borderRadius: 5,
  padding: compact ? '6px 10px' : 24,
  cursor: 'pointer',
  transition: 'border-color 0.2s, background-color 0.2s',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[50],
  },
  '&.dragover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light,
    opacity: 0.8,
  },
}))

const FileInputPlaceholderIcon = styled('div', {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: compact ? 0 : 8,
  flexShrink: 0,
  '& svg': {
    width: compact ? 18 : 50,
    height: compact ? 18 : 50,
    marginTop: compact ? 0 : 10,
    backgroundColor: theme.palette.grey[100],
    borderRadius: compact ? 4 : 8,
    padding: compact ? 3 : 8,
  },
}))

const FileInputHelperText = styled('p', {
  name: 'FileInputHelperText',
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
  textAlign: compact ? 'left' : 'center',
  margin: 0,
  fontSize: compact ? 13 : undefined,
  lineHeight: compact ? 1.3 : undefined,
  '& span': {
    color: theme.palette.blue[800],
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}))

const HiddenFileInput = styled('input')({
  display: 'none',
})

type UploadFileDropzoneProps = {
  uploadStatus: UploadStatus
  fileTypesAccepted?: string
  placeholderIcon?: React.ReactNode
  multiple?: boolean
  fileTypeLabel?: string
  slot: string
  resource_prefix: ResourcePrefix
  onFilesSelected?: (files: FileRecord[]) => void
  style?: React.CSSProperties
  hideWhenPopulated?: boolean
  maxFileSizeBytes?: number
  autoUpload?: boolean
  onUploadSuccess?: (uploadedFiles: FileRecord[]) => void
  compact?: boolean
}

export const UploadFileDropzone = ({
  uploadStatus,
  fileTypesAccepted = '',
  placeholderIcon = <UploadIcon />,
  multiple = false,
  fileTypeLabel,
  slot,
  resource_prefix,
  onFilesSelected,
  style,
  hideWhenPopulated,
  maxFileSizeBytes,
  autoUpload = false,
  onUploadSuccess,
  compact = false,
}: UploadFileDropzoneProps) => {
  const translate = useTranslate()
  const translateParse = useTranslateParse()
  const notify = useNotify()
  const { fileRecords, setFileRecords, uploadBySlot } = useFileUploadContext()
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropzoneRef = useRef<HTMLDivElement>(null)

  // Get file records for this slot to determine if we should show "more" text
  const slotFileRecords = fileRecords.filter((record) => record.slot === slot)
  const hasFiles = slotFileRecords.length > 0

  const fileTypeLabelText = useMemo(() => {
    return fileTypeLabel ? fileTypeLabel : multiple ? translate('files') : translate('file')
  }, [fileTypeLabel, multiple, translate])

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return

      const fileArray = Array.from(files)
      let fileArrayToProcess: File[]

      // Validate file sizes if maxFileSizeBytes is specified
      if (maxFileSizeBytes !== undefined) {
        const oversizedFiles = fileArray.filter((file) => file.size > maxFileSizeBytes)

        if (oversizedFiles.length > 0) {
          const maxSizeFormatted = formatFileSize(maxFileSizeBytes, translate)
          const errorMessage = translate('File/s exceeds the maximum size of %{maxSize}', {
            maxSize: maxSizeFormatted,
          })
          notify(errorMessage, 'warning')

          // Filter out oversized files
          const validFiles = fileArray.filter((file) => file.size <= maxFileSizeBytes)

          // If no valid files remain, return early
          if (validFiles.length === 0) {
            return
          }

          // Continue with only valid files
          fileArrayToProcess = validFiles
        } else {
          // All files are valid, process all
          fileArrayToProcess = fileArray
        }
      } else {
        // No size limit, process all files
        fileArrayToProcess = fileArray
      }

      // Get current records for this slot
      const currentRecords = fileRecords.filter((r) => r.slot === slot)

      // Create new pending records from selected files
      const newPendingRecords = fileArrayToProcess.map((file) => createPendingFileRecord(file, slot, resource_prefix))

      // In single-file mode, replace all records; in multiple mode, append to pending
      let updatedSourceRecords: FileRecord[]
      if (multiple) {
        // Multiple mode: keep existing records and append new pending files
        updatedSourceRecords = [...currentRecords, ...newPendingRecords]
      } else {
        // Single file mode: replace all records with the new file
        updatedSourceRecords = [newPendingRecords[0]]
      }

      // Update context
      setFileRecords(slot, updatedSourceRecords, resource_prefix)

      // Call optional callback
      onFilesSelected?.(newPendingRecords)

      // If autoUpload is enabled, trigger upload immediately for this slot only
      if (autoUpload && newPendingRecords.length > 0) {
        // Small delay to ensure context is updated
        setTimeout(() => {
          uploadBySlot(slot)
            .then((uploadedFiles) => {
              onUploadSuccess?.(uploadedFiles)
            })
            .catch((error) => {
              // Error handling is done in the context
              console.error('Auto-upload failed:', error)
            })
        }, 0)
      }
    },
    [
      slot,
      multiple,
      resource_prefix,
      fileRecords,
      setFileRecords,
      onFilesSelected,
      maxFileSizeBytes,
      autoUpload,
      uploadBySlot,
      notify,
      translate,
    ]
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files)
      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [handleFiles]
  )

  const handleClick = useCallback(() => {
    if (uploadStatus !== 'uploading' && uploadStatus !== 'uploadFailed') {
      fileInputRef.current?.click()
    }
  }, [uploadStatus])

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (uploadStatus !== 'uploading' && uploadStatus !== 'uploadFailed') {
        setIsDragging(true)
      }
    },
    [uploadStatus]
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      if (uploadStatus !== 'uploading' && uploadStatus !== 'uploadFailed') {
        handleFiles(e.dataTransfer.files)
      }
    },
    [handleFiles, uploadStatus]
  )

  const isDisabled = isUploadDisabled(uploadStatus)

  // Hide dropzone if hideWhenPopulated is true and there are files
  // If hideWhenPopulated is not provided, default to true when in single-file mode, and false when in multiple-file mode
  const doHideWhenPopulated = hideWhenPopulated !== undefined ? hideWhenPopulated : !multiple
  if (doHideWhenPopulated && hasFiles) {
    return null
  }

  return (
    <FileInputPlaceholderContainer
      ref={dropzoneRef}
      compact={compact}
      className={classNames('dropzone-container', isDragging && !isDisabled ? 'dragover' : '')}
      onClick={isDisabled ? undefined : handleClick}
      onDragOver={isDisabled ? undefined : handleDragOver}
      onDragLeave={isDisabled ? undefined : handleDragLeave}
      onDrop={isDisabled ? undefined : handleDrop}
      style={{
        ...(isDisabled ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' } : {}),
        ...style,
      }}
    >
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept={fileTypesAccepted}
        multiple={multiple}
        onChange={handleFileSelect}
      />
      <FileInputPlaceholderIcon compact={compact}>{placeholderIcon}</FileInputPlaceholderIcon>
      <FileInputHelperText compact={compact}>
        {translateParse(
          'Drag & drop or <a>browse %{fileType}<a>',
          {
            a: (label) => <span>{label}</span>,
          },
          { fileType: fileTypeLabelText }
        )}{' '}
      </FileInputHelperText>
    </FileInputPlaceholderContainer>
  )
}
